import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Collapse, Row } from "reactstrap";
import { useIsMutating } from "@tanstack/react-query";
import { Dropdown } from "rsuite";
import { useAtom } from "jotai";

import { SubitemCardsStateAtom } from "../../Drawers/Records/ViewRecordDrawer";
import UpdateSubitemDrawer from "../../Drawers/Subitems/UpdateSubitemDrawer";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import TabbedMultiSubitem from "../../Records/Subitems/TabbedMultiSubitem";
import MultiSubitemEditor from "../../Records/Subitems/MultiSubitemEditor";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import ViewRecordDrawer from "../../Drawers/Records/ViewRecordDrawer";
import { generatePrefill } from "../../../utils/QuickCreateUtils";
import MultiSourceSummaryView from "./MultiSourceSummaryView";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { subitemDrawerAtom } from "../../../atoms/PageAtoms";
import SummaryCardTableView from "./SummaryCardTableView";
import useUser from "../../../api/api-hooks/auth/useUser";
import SummaryCardToolbar from "./SummaryCardToolbar";
import AppTooltip from "../../../elements/AppTooltip";
import FieldUtils from "../../../utils/FieldUtils";
import useToggle from "../../../hooks/useToggle";
import SummaryCardView from "./SummaryCardView";
import StrUtils from "../../../utils/StrUtils";

/**
 * SummaryCardWidget - A widget that displays a summary of a record, primarily used for referenced records / Subitems
 * within a record. There are 2 modes for MultiReference fields: "table" and "card".
 * @param {*} records - Expects an array of record(s)
 * @param {*} fieldSchema - The records schema object for the reference records
 */
const SummaryCardWidget = ({
  module,
  records,
  fieldSchema,
  parentRecord,
  maxHeight = 400,
  readOnly = true,
  reusableViewReferenceFields,
}) => {
  const [page, setPage] = useState(0);
  const [viewTypeState, setViewTypeState] = useAtom(SubitemCardsStateAtom);
  const [sourceType, setSourcetype] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [collapse, toggleCollapse] = useToggle(false);
  const [updateSubitem, toggleUpdateSubitem] = useToggle(false);
  const [isDuplicating, toggleDuplicateSubitem] = useToggle(false);
  const [viewModule, setViewModule] = useState(null);
  const isMultipleRecords = records?.length > 1;
  const isReference = FieldUtils.fieldTypeIsReference(fieldSchema?.FieldType);
  const [subitemDrawer, setSubitemDrawer] = useAtom(subitemDrawerAtom);
  const [subitemAdding, setSubitemAdding] = useState(false);
  const isSubitem = FieldUtils.fieldTypeIsSubitem(fieldSchema?.FieldType);
  const { updateRecord, recordUpdating } = useUpdateRecord();
  const [prefill, setPrefill] = useState(null);
  const { user } = useUser();
  const multiReferenceUpdating = useIsMutating({
    mutationKey: ["updateRecordItems", parentRecord.id],
  });
  const referenceUpdating = useIsMutating({
    mutationKey: ["updateRecord", parentRecord.id],
  });

  const viewType = viewTypeState[fieldSchema?.APIName] || "Card";

  const setViewType = (newViewType) => {
    setViewTypeState((prev) => ({
      ...prev,
      [fieldSchema?.APIName]: newViewType,
    }));
  };

  const recordsRef = useRef();
  recordsRef.current = records;

  const clearSubitem = () => {
    updateRecord({
      field: fieldSchema,
      payload: {
        [fieldSchema.APIName]: null,
      },
      moduleAPIName: parentRecord._ModuleAPIName,
      recordID: parentRecord.id || parentRecord._id,
    });
  };

  const switchView = (newViewType) => () => {
    setViewType(newViewType);
  };
  const switchPage = (newPage) => () => {
    if (newPage < 0) return setPage(records.length - 1);
    if (newPage >= records.length) return setPage(0);

    setPage(newPage);
  };

  const viewCurrentRecord = () => {
    setViewRecord(records[page]);
    setViewModule(
      getModule(records[page]?.ModuleAPIName || records[page]?._ModuleAPIName)
    );
  };

  const closeCurrentRecord = () => {
    setViewRecord(null);
    setViewModule(null);
  };

  const multiSubitemCustomOptions = useMemo(() => {
    const isCard = viewType === "Card";

    return (
      <>
        {isCard && !subitemAdding && (
          <Button
            className="btn-icon-only"
            size="sm"
            color="primary"
            style={{ height: 23, width: 24 }}
            onClick={() => setSubitemAdding(true)}
            disabled={fieldSchema?.ReadOnly}
          >
            <i className="mdi mdi-plus" style={{ top: 0, left: 0 }} />
          </Button>
        )}
        {subitemAdding || isDuplicating ? (
          <Button
            onClick={() => {
              if (subitemAdding) setSubitemAdding(false);
              if (isDuplicating) {
                toggleDuplicateSubitem();
                setPrefill(null);
              }
            }}
            outline
            size="sm"
          >
            Done
          </Button>
        ) : (
          <AppTooltip
            useTooltip
            enterable={false}
            content={isCard ? "View as Table" : "View as Cards"}
          >
            <Button
              className="btn-icon-only"
              size="sm"
              style={{ height: 23, width: 30 }}
              outline
              onClick={switchView(isCard ? "Table" : "Card")}
              disabled={fieldSchema?.ReadOnly}
            >
              <i
                className={
                  isCard
                    ? "mdi mdi-table-large"
                    : "mdi mdi-card-multiple-outline"
                }
                style={{ top: 0, left: 0 }}
              />
            </Button>
          </AppTooltip>
        )}
      </>
    );
  }, [
    page,
    records,
    fieldSchema?.SubitemDisplayMode,
    viewType,
    toggleUpdateSubitem,
    subitemAdding,
    isDuplicating,
  ]);

  const clearSubitemRef = useRef();
  clearSubitemRef.current = clearSubitem;

  const subitemOptions = useMemo(
    () => (
      <Dropdown.Menu>
        <DropdownOptionWithIcon
          name="Edit Subitem"
          onClick={toggleUpdateSubitem}
          icon="mdi mdi-pencil"
        />
        <DropdownOptionWithIcon
          name="Clear Subitem"
          onClick={clearSubitemRef.current}
          disabled={
            !records ||
            !records?.length ||
            (records.length === 1 && records[0] === null)
          }
          icon="mdi mdi-close-octagon"
        />
      </Dropdown.Menu>
    ),
    [toggleUpdateSubitem, records]
  );

  // For Reference types only - Also checks to see if they have multiple sources or not
  const multiReferenceSources = useMemo(() => {
    if (!FieldUtils.fieldTypeIsReference(fieldSchema?.FieldType)) return null;

    if (Array.isArray(fieldSchema?.SourcesList)) {
      setSourcetype("MultiSource");
    } else {
      setSourcetype("SingleSource");
    }

    if (Array.isArray(fieldSchema.SourcesList)) {
      const aux = fieldSchema.SourcesList?.map((s) => {
        return s.split("Modules.", 2)[1];
      });
      return [...aux];
    }
    return fieldSchema.SourcesList;
  }, [fieldSchema]);

  const getOptions = () => {
    const type = fieldSchema?.FieldType;

    if (type === "MultiReference") {
      return null; // TODO: Readd when relevant. (Use cards/Table toggle instead)
    }

    if (type === "Subitem") {
      return subitemOptions;
    }

    if (type === "MultiSubitem") {
      return null;
    }

    return null;
  };

  const getFields = () => {
    if (isReference) {
      return fieldSchema?.RecordSummaryFields?.[records?.[page]?._ModuleAPIName]
        ?.Fields;
    }

    if (isSubitem) {
      return fieldSchema.SubitemFields;
    }

    return [];
  };

  const getPrefill = () => {
    let prefillPairs = [];
    const ignoredFields = [
      "id",
      "Created",
      "CreatedBy",
      "LastUpdated",
      "LastUpdatedBy",
      "_ModuleAPIName",
      "_RecordName",
      "_SubitemParentAPIName",
    ];

    if (!isSubitem || !records?.[page]) return;

    for (let i = 0; i < Object.keys(records?.[page]).length; i++) {
      const field = Object.keys(records?.[page])[i];
      const value = records?.[page]?.[field];

      if (ignoredFields.includes(field)) continue;

      prefillPairs.push({ Field: field, Value: value });
    }

    return prefillPairs;
  };

  useEffect(() => {
    if (fieldSchema?.FieldType === "MultiSubitem" && !subitemDrawer?.field) {
      setSubitemDrawer({
        open: true,
        parentRecord,
        field: fieldSchema,
        displayMode: fieldSchema.displayMode,
        moduleAPIName: parentRecord._ModuleAPIName,
        reusableViewReferenceFields,
      });
    }
  }, [
    fieldSchema?.FieldType,
    fieldSchema?.RecordSummaryFields,
    multiReferenceSources,
    fieldSchema,
    parentRecord,
    setSubitemDrawer,
    subitemDrawer,
  ]);

  //Reference loading state
  if (!!multiReferenceUpdating || !!referenceUpdating) {
    return (
      <div className="border border-ultralight bg-superlight rounded-sm my-4">
        <SummaryCardToolbar
          fieldSchema={fieldSchema}
          parentRecord={parentRecord}
          record={records?.[page]}
          records={records}
          id={records?.[page]?.id || records?.[page]?._id}
          options={getOptions()}
          customOptions={
            fieldSchema?.FieldType === "MultiSubitem"
              ? multiSubitemCustomOptions
              : undefined
          }
          collapse={collapse}
          toggleCollapse={toggleCollapse}
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
        <div className="m-4">
          <SkeletonLoader height={200} width={"100%"} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border border-ultralight bg-ultralight btn-oauth rounded-sm my-4">
        <SummaryCardToolbar
          title={fieldSchema?.Name}
          description={fieldSchema?.Description}
          fieldSchema={fieldSchema}
          parentRecord={parentRecord}
          record={records?.[page]}
          records={records}
          id={records?.[page]?.id || records?.[page]?._id}
          options={getOptions()}
          customOptions={
            fieldSchema?.FieldType === "MultiSubitem"
              ? multiSubitemCustomOptions
              : undefined
          }
          onEdit={null}
          viewType={viewType}
          collapse={collapse}
          toggleCollapse={toggleCollapse}
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
        <Collapse isOpen={!collapse}>
          {sourceType === "MultiSource" && (
            <>
              {viewType === "Card" ? (
                <MultiSourceSummaryView
                  records={records ? records : []}
                  sources={[...multiReferenceSources]}
                  fieldSchema={fieldSchema}
                  mode="SummaryCard"
                  collapse={collapse}
                />
              ) : (
                <MultiSourceSummaryView
                  records={records ? records : []}
                  sources={[...multiReferenceSources]}
                  fieldSchema={fieldSchema}
                  mode="SummaryTable"
                />
              )}
            </>
          )}
          {sourceType !== "MultiSource" && (
            <>
              {viewType === "Card" ? (
                recordUpdating ? (
                  <div className="py-2 px-3">
                    <SkeletonLoader />
                  </div>
                ) : (
                  <>
                    {subitemAdding || isDuplicating ? (
                      <div className="p-2">
                        <MultiSubitemEditor
                          module={module}
                          parentRecord={parentRecord}
                          fieldSchema={fieldSchema}
                          prefill={prefill}
                          setPrefill={setPrefill}
                          currentValues={records}
                          setSubitemAdding={setSubitemAdding}
                          summaryWidget={true}
                          reusableViewReferenceFields={
                            reusableViewReferenceFields
                          }
                        />
                      </div>
                    ) : (
                      <>
                        <SummaryCardView
                          onView={
                            viewType === "Card" && isReference
                              ? viewCurrentRecord
                              : null
                          }
                          record={records?.[page]}
                          parentRecord={parentRecord}
                          fieldSchema={fieldSchema}
                          fields={getFields()}
                          maxHeight={maxHeight}
                          collapse={collapse}
                          useTitle={isReference}
                          readOnly={readOnly}
                          module={module}
                          moduleAPIName={parentRecord?._ModuleAPIName}
                          reusableViewReferenceFields={
                            reusableViewReferenceFields
                          }
                        />
                      </>
                    )}
                  </>
                )
              ) : null}
              {viewType === "Table" && (
                <>
                  {isSubitem && (
                    <>
                      {subitemAdding || isDuplicating ? (
                        <div className="p-2">
                          <MultiSubitemEditor
                            isDuplicating={isDuplicating}
                            toggleDuplicateSubitem={toggleDuplicateSubitem}
                            module={module}
                            parentRecord={parentRecord}
                            fieldSchema={fieldSchema}
                            prefill={prefill}
                            setPrefill={setPrefill}
                            currentValues={records}
                            setSubitemAdding={setSubitemAdding}
                            reusableViewReferenceFields={
                              reusableViewReferenceFields
                            }
                          />
                        </div>
                      ) : (
                        <div className="p-2">
                          <TabbedMultiSubitem
                            subitemDuplicating={isDuplicating}
                            toggleDuplicateSubitem={toggleDuplicateSubitem}
                            module={module}
                            fieldSchema={fieldSchema}
                            records={records}
                            parentRecord={parentRecord}
                            showTitle={false}
                            setAddingSubitem={setSubitemAdding}
                            subitemAdding={subitemAdding}
                            managePrefill={managePrefill}
                            summaryWidget={true}
                            reusableViewReferenceFields={
                              reusableViewReferenceFields
                            }
                          />
                        </div>
                      )}
                    </>
                  )}
                  {isReference && (
                    <>
                      <SummaryCardTableView
                        records={records}
                        fieldSchema={fieldSchema}
                        moduleAPIName={viewModule}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Collapse>
      </div>
      {viewType === "Card" &&
        !collapse &&
        isMultipleRecords &&
        sourceType !== "MultiSource" && (
          <>
            {!subitemAdding && (
              <div className="pb-4 mt--3">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <Button
                      size="sm"
                      color="dark"
                      outline
                      className="btn-icon-only "
                      style={{ height: 25, width: 25 }}
                    >
                      <i
                        className="mdi mdi-chevron-left"
                        onClick={switchPage(page - 1)}
                        style={{
                          fontSize: 23,
                          lineHeight: 1,
                          position: "relative",
                          top: 1,
                        }}
                      />
                    </Button>
                  </Col>
                  <Col>
                    <div className="text-center">
                      <small
                        style={{ fontSize: 14 }}
                        className="font-weight-500"
                      >
                        {StrUtils.formatNumber(page + 1)} of{" "}
                        {StrUtils.formatNumber(records?.length)}
                      </small>
                    </div>
                  </Col>
                  <Col xs="auto">
                    <Button
                      size="sm"
                      color="dark"
                      outline
                      className="btn-icon-only"
                      style={{ height: 25, width: 25 }}
                    >
                      <i
                        className="mdi mdi-chevron-right"
                        onClick={switchPage(page + 1)}
                        style={{
                          fontSize: 23,
                          lineHeight: 1,
                          position: "relative",
                          top: 1,
                        }}
                      />
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
          </>
        )}
      <ViewRecordDrawer
        open={viewRecord !== null}
        onClose={closeCurrentRecord}
        recordID={viewRecord?.id}
        module={viewModule}
      />
      {isSubitem && (
        <UpdateSubitemDrawer
          open={updateSubitem}
          onClose={toggleUpdateSubitem}
          records={
            fieldSchema?.FieldType === "Subitem" ? records?.[page] : records
          }
          fieldSchema={fieldSchema}
          parentRecord={parentRecord}
          module={module}
          addSubitemMode={true}
          prefill={getPrefill() ? getPrefill() : []}
        />
      )}
    </>
  );

  function managePrefill(value) {
    const variableVals = {
      user: {
        id: user?.id,
        _RecordName: user?.Name,
        FirstName: StrUtils.getFirstName(user?.Name),
        LastName: StrUtils.getLastName(user?.Name),
        ModuleAPIName: "Contacts",
      },
    };
    const prefill = value?.PrefillPairs;
    generatePrefill(
      prefill,
      variableVals,
      fieldSchema.SubitemFields,
      setPrefill
    );
  }
};

export default SummaryCardWidget;
